import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Grid,
  SvgIcon,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowBack } from '@mui/icons-material';
import throttle from 'lodash/throttle';
import { Helmet } from 'react-helmet-async';
import {
  Resolver,
  useForm,
  UseFormGetValues,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { Asset } from '../../../../modules/loan/asset';
import { ProjectTypes } from '../../../../modules/loan/projectType';
import { Installer } from '../../../../modules/installer';
const { BorrowerForm } = Installer.UI;
import { useQuery, useQueryClient } from 'react-query';
import {
  LoanTypeMapping,
  ProductFamily,
  ProductModel,
  Rates,
  SpecialPaymentProducts,
  type Account,
} from '../../../../../../../types/api';
import {
  INSTALLER_DASHBOARD_PATH,
  INSTALLER_DOCUMENT_TYPE,
  REQUIRED_DOCUMENT_REQUIRED_FOR_STEP,
} from '../../../../constants';
import useAuth from '../../../../hooks/useAuth';
import { useDisclosure } from '../../../../hooks/useDisclosure';
import { INSTALLER_APPS_QUERY } from '../../../../queries';
import { applicationAPI } from '../../../../services/apis';
import {
  getExternalApplication,
  getRates,
} from '../../../../services/apis/InstallerAPIs';
import noticeError from '../../../../utils/errors/noticeError';
import {
  getAtPath,
  isDevOrStagingEnvironment,
  populateFakeData,
  traverse,
  isConsultant as isConsultantCheck,
} from '../../../../utils/helpers';
import { Tracker } from '../../../../utils/tracker';
import InfoDialogueWithCtaButton from '../../../InfoDialogueWithCtaButton';
import LoadingBox from '../../../LoadingBox';
import { sendMonthlyPaymentRequest } from '../../../loan_application/utils/helpers';
import Lottie from '../../../lottie/Lottie';
import DocumentSuccess from './../icons/DocumentSuccess.svg';
import animationData from './../icons/Loading.json';
import staticImage from './../icons/LoadingStatic.svg';
import BusinessContactInformationFieldset from './fieldsets/BusinessContactInformation';
import LoanInformation from './fieldsets/LoanInformation';
import NonSolarProjectComponents from './fieldsets/NonSolarProjectComponents';
import ProjectAssets from './fieldsets/ProjectAssets';
import SolarProjectInformation from './fieldsets/SolarProjectInformation';
import {
  getValidationSchema,
  InitialValues,
  NewBorrowerUrlParams,
  safeParseNewBorrowerUrlParams,
} from './schema';
import { DocumentsUploadSection } from './fieldsets/DocumentsUploadSection';
import { NewBorrowerNavigationState } from '../../../../pages/pricing-calculator/types';
import {
  applyNewBorrowerFormEditsToLoanParams,
  serializeLoanParamsToUrlQuery,
} from '../../../../pages/pricing-calculator/utils';
import AdditionalInformationFieldset from './fieldsets/AdditionalInformation';
import PriceLockAcknowledgement from './PriceLockAcknowledgement';

export type SolarEconomics = {
  economics?: {
    loanAmount?: number | string;
    interestRate?: number;
    loanProductId?: string;
    productId?: string;
    y1saving?: number;
    systemLocation?: string;
    y1production?: number;
    systemSize?: number;
    recInfo?: string;
    isSpecial?: string;
    specialPaymentPercentage?: number;
  };
};

const getInitialValues = (
  params?: NewBorrowerUrlParams | null,
  ratesData?: Rates,
  account?: Account | null,
  milestonePayments?: 'Yes' | 'No'
): InitialValues & SolarEconomics => {
  const rates = Object.values(ratesData?.current ?? {}).flat();
  const product = params?.productId
    ? rates.find((rate) => rate.product === params?.productId)
    : params?.loanType && ratesData?.current[params.loanType]
    ? ratesData.current[params.loanType]?.find(
        (rate) =>
          rate.active &&
          rate.loan_term_years === params?.loanYears?.toString() &&
          rate.amortization_years ===
            (params?.amortizationYears ?? params?.loanYears)?.toString() &&
          rate.interest_rate === params?.interestRate
      )
    : undefined;

  // loanTerm is term + amort with leading 0s.  e.g. '0505' or '1020'
  const loanTermYears = product?.loan_term_years.padStart(2, '0');
  const amortizationYears = product?.amortization_years.padStart(2, '0');
  const loanTerm = product ? `${loanTermYears}${amortizationYears}` : undefined;

  const mounting = {
    roof: params?.mountingType?.includes('Roof Mount') ?? false,
    ground: params?.mountingType?.includes('Ground Mount') ?? false,
    carport: params?.mountingType?.includes('Carport') ?? false,
    other: params?.mountingType?.includes('Other') ?? false,
    otherValue: '',
  };

  return {
    borrower: {
      name: params?.businessName ?? '',
    },
    loanType: product?.product_family ?? params?.loanType,
    loanTerm,
    contact: {
      first_name: params?.firstName ?? '',
      last_name: params?.lastName ?? '',
      position_title: params?.title ?? '',
      email: params?.email ?? '',
      phone: params?.phone ?? '',
    },
    application: {
      dealer_fee: product?.id ?? null,
      sys_prop_address_state: params?.state ?? params?.systemLocation ?? '',
      rec_info: params?.recInfo ?? null,
      loan_product: product?.product ?? '',
      has_non_solar_component: '',
      non_solar_amount: 0,
      energy_storage_amount: params?.enhancementCosts?.['Energy storage'] ?? 0,
      ev_charging_amount: params?.enhancementCosts?.['EV charging'] ?? 0,
      roof_repair_amount: params?.enhancementCosts?.['Roof repairs'] ?? 0,
      other_non_solar_amount: params?.enhancementCosts?.['Other'] ?? 0,
      system_mounting_hardware: '',
      non_solar_components: '',
      loan_amount: params?.loanAmount ?? 0,
      project_timeline_estimation: params?.timeline ?? '',
      deal_description_and_context: params?.dealContext ?? '',
      system_size_in_k_ws: params?.systemSizeKw ?? params?.systemSize ?? 0,
      year_1_system_production_kwh:
        params?.year1ProductionKwh ?? params?.y1production ?? 0,
      year_1_total_solar_savings:
        params?.year1SavingsUsd ?? params?.y1saving ?? 0,
      total_system_cost: params?.systemCostUsd ?? undefined,
      special_payment_percentage: params?.specialPaymentPercentage ?? undefined,
      borrower_advance: milestonePayments ?? params?.milestonePayments ?? '',
      is_flagged_non_profit_by_installer: params?.isNonProfit ?? '',
      external_source_id: params?.externalId ?? undefined,
      project_nickname: '',
    },
    loan_proposal: {
      file: undefined as unknown as File,
    },
    utility_bills: [],
    incentive_documents: [],
    mounting,
    mountingChecked:
      mounting.roof || mounting.ground || mounting.carport || mounting.other,
    nonSolar: {
      hasNonSolarComponent: params?.includesEnhancements ?? false,
      nonSolarAmount: params?.nonSolarCost ?? 0,
      energyStorage:
        params?.fundedEnhancements?.includes('Energy storage') ?? false,
      evCharging: params?.fundedEnhancements?.includes('EV charging') ?? false,
      roofRepair: params?.fundedEnhancements?.includes('Roof repairs') ?? false,
      other: params?.fundedEnhancements?.includes('Other') ?? false,
      otherValue: '',
    },
    nonSolarChecked: false,
    projectAssets:
      params?.projectAssets ?? BorrowerForm.getDefaultProjectAssets(account),
    projectAssetsChecked: false,
    economics: {
      loanAmount: params?.loanAmount,
      interestRate: product?.interest_rate,
      loanProductId: product?.id,
      productId: product?.id ?? '',
      y1saving: params?.year1SavingsUsd ?? params?.y1saving,
      systemLocation: params?.state ?? params?.systemLocation,
      y1production: params?.year1ProductionKwh ?? params?.y1production,
      systemSize: params?.systemSizeKw ?? params?.systemSize,
      recInfo: params?.recInfo,
      isSpecial: params?.isSpecial,
      specialPaymentPercentage: params?.specialPaymentPercentage,
    },
  };
};

const touchAllFields = async (
  setValue: UseFormSetValue<InitialValues>,
  getValues: UseFormGetValues<InitialValues>
) => {
  const fields = [
    'borrower.name',
    'loanType',
    'loanTerm',
    'contact.first_name',
    'contact.last_name',
    'contact.position_title',
    'contact.email',
    'contact.phone',
    'application.dealer_fee',
    'application.sys_prop_address_state',
    'application.rec_info',
    'application.loan_product',
    'application.has_non_solar_component',
    'application.non_solar_amount',
    'application.energy_storage_amount',
    'application.ev_charging_amount',
    'application.roof_repair_amount',
    'application.other_non_solar_amount',
    'application.system_mounting_hardware',
    'application.non_solar_components',
    'application.loan_amount',
    'application.project_timeline_estimation',
    'application.deal_description_and_context',
    'application.system_size_in_k_ws',
    'application.year_1_system_production_kwh',
    'application.year_1_total_solar_savings',
    'application.total_system_cost',
    'application.special_payment_percentage',
    'application.borrower_advance',
    'application.interest_payer_during_construction',
    'application.is_flagged_non_profit_by_installer',
    'application.external_source_id',
    'application.project_nickname',
    'mounting.roof',
    'mounting.carport',
    'mounting.ground',
    'mounting.other',
    'nonSolar.hasNonSolarComponent',
    'nonSolar.nonSolarAmount',
    'nonSolar.roofRepair',
    'nonSolar.other',
    'nonSolar.energyStorage',
    'nonSolar.evCharging',
    'nonSolar.otherValue',
  ] as const;

  fields.forEach((field) => {
    setValue(field, getValues(field) as string, {
      shouldTouch: true,
      shouldValidate: true,
      shouldDirty: true,
    });
  });
};

export default function NewBorrowerForm() {
  const { user, account } = useAuth();
  const [params] = useSearchParams();
  const locationState = useLocation().state;
  const navigate = useNavigate();
  const tracker = Tracker.getInstance();
  const [isLoading, setIsLoading] = useState(false);
  const parsedParams = safeParseNewBorrowerUrlParams(params);

  const milestonePaymentsAnswer = (parsedParams?.milestonePayments ??
    (locationState as NewBorrowerNavigationState | null)?.loanParams
      ?.milestonePayments) as 'Yes' | 'No' | undefined;

  const [fileIds, setFileIds] = useState<Record<string, string>>({});

  const queryClient = useQueryClient();
  const successModal = useDisclosure();
  const loadingModal = useDisclosure();
  const [successModalData, setSuccessModalData] = useState<{
    businessName: string | null;
    email: string | null;
  }>({
    businessName: null,
    email: null,
  });

  const isV2Active = true; // This will always be true in this component. We can get rid of this flag once we deprecate the old form.
  const installerHasFlex = Installer.hasFlex(account);
  const installerIsOnlySolar = Installer.isOnlySolar(account);
  const showAssetCards =
    BorrowerForm.isProjectAssetInformationSectionVisible(account);

  // Built per-validation (not statically) so the schema can react to the
  // current asset selection: the Solar Project Information fields and the
  // Milestone borrower_advance question are both gated on the project type,
  // which is derived from the selected assets.
  const resolver: Resolver<InitialValues & SolarEconomics> = useMemo(
    () => (values, context, options) => {
      const selectedAssetTypes = Asset.getSelectedTypes(values.projectAssets);
      const projectType = ProjectTypes.forAssetTypes(selectedAssetTypes);
      const requireBorrowerAdvance =
        Installer.Programs.hasApprovedProgramForProjectType(
          account?.installer_programs,
          projectType
        ) || isConsultantCheck(account?.type);
      return zodResolver(
        getValidationSchema(
          user?.email ?? '',
          requireBorrowerAdvance,
          isV2Active,
          account,
          selectedAssetTypes
        )
      )(values, context, options);
    },
    [
      user?.email,
      isV2Active,
      account?.contractor_type,
      account?.installer_programs,
      account?.type,
    ]
  );

  const [applicationDraftId, setApplicationDraftId] = useState<string>('');
  const isDraftCompletedRef = useRef(false);

  const sanitizedApplication = () => {
    const values = { ...getValues() };
    const {
      loan_proposal: proposalFile,
      utility_bills: utilityBillFiles,
      incentive_documents: incentiveDocumentFiles,
      ...application
    } = values;

    application.application.dealer_fee = product?.rateId;

    delete application.loanTerm;
    delete application.loanType;

    // Non-consultants store the specific approved Milestone program type as
    // borrower_advance; consultants (and "No") keep their literal value.
    if (
      application.application.borrower_advance === 'Yes' &&
      !isConsultantCheck(account?.type)
    ) {
      const projectType = ProjectTypes.forAssetTypes(
        Asset.getSelectedTypes(values.projectAssets)
      );
      const program =
        Installer.Programs.findApprovedMilestoneProgramForProjectType(
          account?.installer_programs,
          projectType
        );
      if (program) {
        // Clone first so form state is not touched
        application.application = { ...application.application };
        application.application.borrower_advance = program.type;
      }
    }

    // Clean Non Solar Amounts for django validation
    application.application.non_solar_amount =
      application.nonSolar?.nonSolarAmount;
    if (!values.application?.rec_info) values.application.rec_info = null;
    if (
      !values.nonSolar?.hasNonSolarComponent ||
      values.application.non_solar_amount === ''
    )
      values.application.non_solar_amount = null;
    if (
      !values.nonSolar?.hasNonSolarComponent ||
      values.application.energy_storage_amount === ''
    )
      values.application.energy_storage_amount = null;
    if (
      !values.nonSolar?.hasNonSolarComponent ||
      values.application.ev_charging_amount === ''
    )
      values.application.ev_charging_amount = null;
    if (
      !values.nonSolar?.hasNonSolarComponent ||
      values.application.roof_repair_amount === ''
    )
      values.application.roof_repair_amount = null;
    if (
      !values.nonSolar?.hasNonSolarComponent ||
      values.application.other_non_solar_amount === ''
    )
      values.application.other_non_solar_amount = null;
    // Handle Non Solar MultiSelect
    if (values.nonSolar?.hasNonSolarComponent) {
      const nonSolarComponents =
        (values.nonSolar?.energyStorage ? 'Energy Storage;' : '') +
        (values.nonSolar?.evCharging ? 'EV Charging;' : '') +
        (values.nonSolar?.roofRepair ? 'Roof Repairs/Replacement;' : '') +
        (values.nonSolar?.other
          ? 'Other: ' + values.nonSolar?.otherValue?.replace(';', ',') + ';'
          : '');
      application.application.non_solar_components = nonSolarComponents;
    }
    if (application.nonSolar?.hasNonSolarComponent)
      application.application.has_non_solar_component = 'Yes';
    else application.application.has_non_solar_component = 'No';
    delete application.nonSolar;
    // Handle System Mounting Hardware MultiSelect
    const systemMount =
      (values.mounting?.roof ? 'Roof Mount;' : '') +
      (values.mounting?.ground ? 'Ground Mount;' : '') +
      (values.mounting?.carport ? 'Carport;' : '') +
      (values.mounting?.other
        ? 'Other: ' + values.mounting?.otherValue?.replace(';', ',') + ';'
        : '');
    application.application.system_mounting_hardware = systemMount;
    delete application.mounting;
    delete application.mountingChecked;
    delete application.nonSolarChecked;
    delete application.projectAssets;
    delete application.projectAssetsChecked;
    return application;
  };
  const updateApplicationDraft = async () => {
    if (!applicationDraftId) return;
    if (isDraftCompletedRef.current) return;
    await applicationAPI
      .updateDraftApplication(applicationDraftId, sanitizedApplication())
      .catch((err) => {
        console.error('Error updating draft application:', err);
      });
  };

  const updateApplicationDraftDebounced = useCallback(
    throttle(updateApplicationDraft, 5000),
    [applicationDraftId]
  );

  useEffect(() => {
    applicationAPI.createDraftApplication().then((data) => {
      console.log(data);
      setApplicationDraftId(data.application_id);
    });
  }, []);

  const { data: ratesData, isLoading: isRatesLoading } = useQuery(
    ['rates', params.toString()],
    () => getRates(),
    {
      staleTime: 15 * 60 * 1000, // 15 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
      onSuccess(data) {
        if (params.size === 0) return;

        const parsedParams = safeParseNewBorrowerUrlParams(params);
        const milestoneFromNav = (parsedParams?.milestonePayments ??
          (locationState as NewBorrowerNavigationState | null)?.loanParams
            ?.milestonePayments) as 'Yes' | 'No' | undefined;

        const initialValues = getInitialValues(
          parsedParams,
          data,
          account,
          milestoneFromNav
        );
        if (!getValues('loanType') && initialValues.loanType) {
          setValue('loanType', initialValues.loanType);
        }

        if (!getValues('loanTerm') && initialValues.loanTerm) {
          setValue('loanTerm', initialValues.loanTerm);
        }

        if (
          !getValues('application.loan_product') &&
          initialValues.application.loan_product
        ) {
          setValue(
            'application.loan_product',
            initialValues.application.loan_product
          );
        }

        if (
          !getValues('application.borrower_advance') &&
          initialValues.application.borrower_advance
        ) {
          setValue(
            'application.borrower_advance',
            initialValues.application.borrower_advance
          );
        }
      },
      refetchOnWindowFocus: false,
    }
  );
  const { isLoading: isExternalApplicationLoading } = useQuery(
    ['applications', parsedParams?.externalId],
    () =>
      parsedParams?.externalId
        ? getExternalApplication(parsedParams.externalId)
        : null,
    {
      enabled: !!parsedParams?.externalId,
      onSuccess(data) {
        if (data.exist) {
          toast.error(
            'Application already exists with this external source id'
          );
          navigate('/dashboard/borrowers');
        }
      },
      refetchOnWindowFocus: false,
    }
  );
  const initialValues = getInitialValues(
    parsedParams,
    ratesData,
    account,
    milestonePaymentsAnswer
  );

  const currentRates = useMemo(() => {
    if (!ratesData) return [];

    const { current } = ratesData;
    const standard = current[ProductFamily.STANDARD] ?? [];
    const specialPayment = current[ProductFamily.SPECIAL_PAYMENT] ?? [];
    const taxCredit = current[ProductFamily.TAX_CREDIT] ?? [];
    // Must include FLEX or Flex-only submissions can't resolve product on
    // submit — sanitizedApplication() looks `product` up by id and sets
    // dealer_fee from it. Mirrors useInstallerLoanProducts.
    const flex = current[ProductFamily.FLEX] ?? [];

    return [...standard, ...specialPayment, ...taxCredit, ...flex];
  }, [ratesData]);

  const currentProducts: ProductModel[] = currentRates.map((rate) => ({
    id: rate.product ? rate.product : '',
    amortizationYears: rate.amortization_years
      ? parseInt(rate.amortization_years)
      : 0,
    dealerFee: rate.dealer_fee,
    loanTermYears: parseInt(rate.loan_term_years),
    productFamily: rate.product_family,
    rateId: rate.id,
    rate: rate.interest_rate,
    rateScenario: rate.pricing_grouping,
    type: 'current',
  }));

  const {
    control,
    formState,
    setValue,
    watch,
    trigger,
    handleSubmit,
    setError,
    getValues,
  } = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: initialValues,
    resolver,
  });

  const projectAssetsWatch = useWatch({ control, name: 'projectAssets' });
  const selectedAssetTypes = useMemo(
    () => Asset.getSelectedTypes(projectAssetsWatch),
    [projectAssetsWatch]
  );

  const showSolarProjectInformation =
    BorrowerForm.isSolarProjectInformationVisible(selectedAssetTypes);

  const [
    mountingRoof,
    mountingCarport,
    mountingGround,
    mountingOther,
    nonSolarHasNonSolarComponent,
    nonSolarRoofRepair,
    nonSolarOther,
    nonSolarEnergyStorage,
    nonSolarEvCharging,
    loanAmount,
    productId,
    borrowerAdvanceValue,
    loanTypeValue,
  ] = watch([
    'mounting.roof',
    'mounting.carport',
    'mounting.ground',
    'mounting.other',
    'nonSolar.hasNonSolarComponent',
    'nonSolar.roofRepair',
    'nonSolar.other',
    'nonSolar.energyStorage',
    'nonSolar.evCharging',
    'application.loan_amount',
    'application.loan_product',
    'application.borrower_advance',
    'loanType',
  ]);

  const product = useMemo(() => {
    return currentProducts.find((p) => p.id === productId);
  }, [currentProducts, productId]);

  const { isSubmitting, isValid, errors } = formState;

  useEffect(() => {
    const subscription = watch(() => updateApplicationDraftDebounced());
    return () => subscription.unsubscribe();
  }, [watch, updateApplicationDraftDebounced]);

  useEffect(() => {
    trigger('mountingChecked');
  }, [trigger, mountingRoof, mountingCarport, mountingGround, mountingOther]);

  useEffect(() => {
    trigger('nonSolarChecked');
    trigger('nonSolar.otherValue');
  }, [
    trigger,
    nonSolarHasNonSolarComponent,
    nonSolarRoofRepair,
    nonSolarOther,
    nonSolarEnergyStorage,
    nonSolarEvCharging,
  ]);

  // Trigger the loan amount validation when the selected asset types change, since it chould
  // change the min loan amount
  useEffect(() => {
    trigger('application.loan_amount');
  }, [selectedAssetTypes, trigger]);

  useEffect(() => {
    const product = currentProducts.find((p) => p.id === productId);

    const currentRate = watch('economics.interestRate');
    const currentLoanAmount = watch('economics.loanAmount');

    if (product && product.rate !== currentRate) {
      setValue('economics.interestRate', product.rate);
    }
    if (loanAmount !== currentLoanAmount) {
      setValue('economics.loanAmount', loanAmount);
    }
  }, [currentProducts, loanAmount, productId, setValue]);

  useEffect(() => {
    if (
      borrowerAdvanceValue &&
      borrowerAdvanceValue !== 'Yes' &&
      borrowerAdvanceValue !== 'No'
    ) {
      setValue('application.borrower_advance', 'Yes', {
        shouldValidate: false,
      });
    }
  }, [borrowerAdvanceValue, setValue]);

  // Preselect assets based on contractor type when not navigating from the calculator.
  // URL params / navigation state carry projectAssets when coming from PriceMyProjectPage,
  // so we only apply the default when the form is opened fresh (no URL params).
  useEffect(() => {
    if (params.size > 0) return;
    setValue('projectAssets', BorrowerForm.getDefaultProjectAssets(account));
  }, [account?.contractor_type]);

  // Reset specialPaymentPercentage when the user switches to a product family
  // that does not use it (Standard, Flex or Storage)
  useEffect(() => {
    if (!SpecialPaymentProducts.includes(loanTypeValue as ProductFamily)) {
      setValue('application.special_payment_percentage', undefined, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [loanTypeValue, setValue]);

  function milestonePaymentsFromBorrowerAdvance(
    borrowerAdvance: string | null | undefined
  ): 'Yes' | 'No' | undefined {
    if (borrowerAdvance === 'No') return 'No';
    if (borrowerAdvance) return 'Yes';
    return undefined;
  }

  const navigateBackToLoanDetails = async () => {
    const baseLoanParams = (locationState as NewBorrowerNavigationState | null)
      ?.loanParams;

    if (!baseLoanParams) return;

    const loanProductValue = watch('application.loan_product');
    const selectedProduct = currentProducts.find(
      (product) => product.id === loanProductValue
    );

    if (!selectedProduct) {
      // Rates not yet loaded — go back with the base URL unchanged so the user
      // doesn't lose any data.
      navigate(
        `/dashboard/loan-details?${new URLSearchParams(
          serializeLoanParamsToUrlQuery(baseLoanParams)
        )}`
      );
      return;
    }

    setIsLoading(true);
    try {
      const loanSizeValue = watch('application.loan_amount');
      const specialPaymentPercentage =
        watch('application.special_payment_percentage') ?? undefined;

      const monthlyPayment = await sendMonthlyPaymentRequest({
        selectedProduct: {
          ...selectedProduct,
          productFamily: selectedProduct.productFamily as ProductFamily,
        },
        specialPaymentPercentage,
        loanAmount: Number(loanSizeValue),
      });

      const formLoanTerm = watch('loanTerm');
      const formLoanType = watch('loanType');

      const mergedLoanParams = applyNewBorrowerFormEditsToLoanParams(
        baseLoanParams,
        {
          loanSize: loanSizeValue,
          netFunding:
            (Number(loanSizeValue) * (100 - (selectedProduct.dealerFee ?? 0))) /
            100,
          loanType:
            LoanTypeMapping[formLoanType as ProductFamily] ?? 'Standard',
          loanTerm: formLoanTerm
            ? `${parseInt(formLoanTerm.slice(0, 2))}%2F${parseInt(
                formLoanTerm.slice(-2)
              )}`
            : baseLoanParams.loanTerm,
          pricingGrouping: `current.${selectedProduct.rateScenario}`,
          pricingScenario: Number(selectedProduct.rateScenario),
          interestRate: watch('economics.interestRate'),
          y1saving: watch('application.year_1_total_solar_savings'),
          y1production: watch('application.year_1_system_production_kwh'),
          systemSize: watch('application.system_size_in_k_ws'),
          systemLocation: watch('application.sys_prop_address_state'),
          recInfo: watch('application.rec_info'),
          specialPaymentPercentage,
          monthlyPayment,
          productId: selectedProduct.id ?? '',
          dealerFee: selectedProduct.dealerFee ?? 0,
          milestonePayments: milestonePaymentsFromBorrowerAdvance(
            watch('application.borrower_advance')
          ),
          projectAssets: watch('projectAssets'),
        }
      );

      navigate(
        `/dashboard/loan-details?${new URLSearchParams(
          serializeLoanParamsToUrlQuery(mergedLoanParams)
        )}`
      );
    } catch (error: unknown) {
      console.error(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (params.size > 0 && !isRatesLoading && !isExternalApplicationLoading) {
      touchAllFields(setValue, getValues);
    }
  }, [params.toString(), isRatesLoading, isExternalApplicationLoading]);

  const handleFilesUpload = async (file, name, id) => {
    try {
      console.log('Uploading file:', name, file, id);
      const mapping = {
        loan_proposal: BorrowerForm.proposalDocumentType(selectedAssetTypes),
        utility_bills: INSTALLER_DOCUMENT_TYPE.UTILITY_BILLS,
        incentive_documents: INSTALLER_DOCUMENT_TYPE.INCENTIVE_DOCUMENTS,
      };
      const formData = new FormData();
      formData.append('documentType', mapping[name]);
      formData.append('file', file);
      formData.append(
        'required_for_step',
        REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.BORROWER_APP_SUBMISSION
      );
      const response = await applicationAPI
        .uploadDocumentDraft(applicationDraftId, formData)
        .catch((err) => {
          console.error(`Error uploading ${name} document draft:`, err);
        });
      if (!response) {
        throw new Error('Upload failed.');
      }
      file.external_id = response.id;
      setFileIds((prev) => ({
        ...prev,
        [id]: response.id,
      }));

      return true;
    } catch (err) {
      console.error(`Error uploading ${name} document draft:`, err);
      throw err;
    }
  };

  const handleFileRemoval = async (documentExternalId: string) => {
    await applicationAPI
      .deleteDocumentDraft(applicationDraftId, documentExternalId)
      .catch((err) => {
        console.error(`Error deleting document draft:`, err);
      });
  };

  if (isRatesLoading || isExternalApplicationLoading) return <LoadingBox />;

  return (
    <Box
      pt={6.25}
      pb={3}
      display="flex"
      justifyContent="center"
      alignItems="center"
      maxWidth="800px"
      px={3}
      mx="auto"
    >
      <Helmet>
        <title>Initialize New Loan Application</title>
      </Helmet>
      <form
        id="newBorrower"
        role={'form'}
        aria-labelledby="newBorrowerFormTitle"
        onSubmit={handleSubmit(async function _handleSubmit(values) {
          try {
            loadingModal.onOpen();
            const application = sanitizedApplication();
            await updateApplicationDraft();
            isDraftCompletedRef.current = true;
            // Assets are not displayed but preselected by contractor type for Solar-only and Storage-only
            // installers, so toAssetPayloads always reflects the correct selection.
            const assetPayloads = Asset.toAssetPayloads(values.projectAssets);
            await applicationAPI.completeDraftApplication(applicationDraftId, {
              assets: assetPayloads,
            });

            await queryClient.invalidateQueries(INSTALLER_APPS_QUERY);

            setSuccessModalData({
              businessName: application.borrower.name,
              email: application.contact.email,
            });
            loadingModal.onClose();
            successModal.onOpen();
          } catch (err) {
            loadingModal.onClose();
            noticeError(err);
            if (err.data) {
              // Traverse the error object and set the errors
              const errorPaths = traverse(err.data, (val) =>
                Array.isArray(val)
              ) as Parameters<typeof setError>[0][];
              errorPaths.forEach((path) => {
                setError(path, {
                  type: 'custom',
                  message: getAtPath(err.data, path)?.[0],
                });
              });
            }
            toast.error(
              `Error! Borrower not created. ${err.message ? err.message : ''}`
            );
            tracker.track(Tracker.EVENTS.NEW_APPLICANT_ERROR, { error: err });
          }
        })}
      >
        {typeof locationState === 'object' &&
          locationState &&
          'from' in locationState &&
          locationState.from === '/dashboard/loan-details' && (
            <Button
              aria-label="Back to Dashboard"
              variant="text"
              onClick={() => {
                tracker.track(Tracker.EVENTS.NEW_APPLICANT_BACK_CLICKED);
                navigateBackToLoanDetails();
              }}
              sx={{ py: 1, px: 1, ml: -1 }}
              color="inherit"
              startIcon={<ArrowBack fontSize="small" />}
              disabled={isLoading}
            >
              Back to Calculator
            </Button>
          )}
        <Box
          pb={3}
          display="flex"
          flexDirection="column"
          justifyContent="start"
        >
          <Typography variant="h1" id="newBorrowerFormTitle" gutterBottom>
            <b>Initialize New Loan Application</b>
          </Typography>
          <Typography variant="body1">
            Thank you for choosing Sunstone Credit! We look forward to reviewing
            this application.
          </Typography>
          <Box mt={2}>
            <Typography variant="body1">
              {`Please note that if your customer does any of the following they will likely be declined:`}
              <ul>
                <li>Supports the cannabis industry (including CBD and hemp)</li>
                <li>Buys or sells currency (including crypto currency)</li>
                <li>
                  Engages in firearm sales (including the sale of ammunition)
                </li>
              </ul>
            </Typography>
          </Box>
          {isDevOrStagingEnvironment() && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => populateFakeData(setValue, user)}
              sx={{ mr: 2 }}
            >
              Populate Fake Data
            </Button>
          )}
        </Box>
        <Grid container spacing={2} maxWidth={'md'} marginBottom={2}>
          {showAssetCards && (
            <Grid item xs={12}>
              <ProjectAssets
                control={control}
                setValue={setValue}
                account={account}
                trigger={trigger}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <LoanInformation
              control={control}
              setValue={setValue}
              trigger={trigger}
            />
          </Grid>
          <Grid item xs={12}>
            <BusinessContactInformationFieldset control={control} />
          </Grid>
          {showSolarProjectInformation && (
            <Grid item xs={12}>
              <SolarProjectInformation
                control={control}
                setValue={setValue}
                installerHasFlex={installerHasFlex}
              />
            </Grid>
          )}
          {installerIsOnlySolar && (
            <Grid item xs={12}>
              <NonSolarProjectComponents
                control={control}
                setValue={setValue}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <DocumentsUploadSection
              control={control}
              setError={setError}
              onFileChange={handleFilesUpload}
              onFileRemove={handleFileRemoval}
            />
          </Grid>
          <Grid item xs={12}>
            <AdditionalInformationFieldset control={control} />
          </Grid>
        </Grid>

        {loanTypeValue !== ProductFamily.FLEX && <PriceLockAcknowledgement />}

        <DialogActions sx={{ pr: 0 }}>
          {isSubmitting && <CircularProgress size={24} />}
          <Button
            color="primary"
            disabled={isSubmitting}
            component={RouterLink}
            to={`/${INSTALLER_DASHBOARD_PATH}/`}
            onClick={() => {
              tracker.track(Tracker.EVENTS.NEW_APPLICANT_CANCEL);
            }}
          >
            Cancel
          </Button>
          <div
            style={{
              position: 'relative',
            }}
          >
            <Button
              color="primary"
              type="submit"
              variant="contained"
              onClick={() => {
                tracker.track(Tracker.EVENTS.NEW_APPLICANT_SUBMIT);
              }}
              disabled={isSubmitting || !isValid}
            >
              Add
            </Button>
          </div>
        </DialogActions>
      </form>

      <InfoDialogueWithCtaButton
        open={successModal.isOpen}
        mainIcon={
          <SvgIcon component={DocumentSuccess} sx={{ fontSize: 125 }} />
        }
        heading={`A new loan application has been successfully created for ${successModalData.businessName}`}
        content={
          <>
            <Typography
              variant="body1"
              color="textSecondary"
              textAlign={'center'}
            >
              The loan applicant will receive an email notification at{' '}
              {successModalData.email} with instructions on how to login to the
              Sunstone portal and access this application. Note: The email
              notification may go to spam/junk email inbox if it is not in their
              email inbox.
            </Typography>
            <br />
            <Typography
              variant="body1"
              color="textSecondary"
              textAlign={'center'}
            >
              Please note the borrower will have 45 days to complete and submit
              this application before it expires. We require applications to be
              completed in 45 days to limit any adverse changes to interest
              rates. If there are special circumstances that will require more
              time to complete the application, an extension can be requested by
              the borrower.
            </Typography>
          </>
        }
        ctaText={'Continue to Portal'}
        ctaFunction={() => {
          successModal.onClose;
          navigate(`/${INSTALLER_DASHBOARD_PATH}/`);
        }}
      />
      <InfoDialogueWithCtaButton
        open={loadingModal.isOpen}
        mainIcon={<></>}
        contentContainerStyle={{ width: '515px' }}
        content={
          <div style={{ textAlign: 'center' }}>
            <Lottie
              defaultImage={staticImage}
              animationData={animationData}
              loop={true}
              autoplay={true}
              style={{ marginTop: '-8rem', width: '200px' }}
            />
            <strong>Please do not close this window.</strong>
            <br /> <span>We are creating your application.</span>
          </div>
        }
        ctaFunction={() => {}}
      />
    </Box>
  );
}
